import { IkePromiseQueue } from '@ikegps/core-shared';
import { Logger } from '../../services';

/**
 * pdfMake's own URL loader fires every image request at once (thousands for a large job, each one
 * possibly generating a composite server-side) and never settles if any single request fails —
 * the export then hangs forever. Reports download their images through here instead: bounded,
 * retried, and failing loudly, then hand pdfMake inline data URLs so it never touches the network.
 */
export const REPORT_IMAGE_CONCURRENCY = 10;
export const REPORT_IMAGE_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

export class ReportImageLoadError extends Error {
	constructor(
		readonly url: string,
		readonly cause: unknown
	) {
		super(`Failed to load report image: ${url} (${cause instanceof Error ? cause.message : String(cause)})`);
		this.name = 'ReportImageLoadError';
	}
}

/** Chunked so large images don't blow the argument limit of `String.fromCharCode`. */
function toBase64(bytes: Uint8Array): string {
	const CHUNK = 0x8000;
	let binary = '';
	for (let i = 0; i < bytes.length; i += CHUNK) {
		binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
	}
	return btoa(binary);
}

async function fetchAsDataUrl(url: string): Promise<string> {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`HTTP ${response.status}`);
	}
	const contentType = response.headers.get('content-type') ?? 'image/jpeg';
	const bytes = new Uint8Array(await response.arrayBuffer());
	return `data:${contentType};base64,${toBase64(bytes)}`;
}

async function fetchWithRetry(url: string, retryDelayMs: number): Promise<string> {
	let lastError: unknown = null;
	for (let attempt = 1; attempt <= REPORT_IMAGE_ATTEMPTS; attempt++) {
		try {
			return await fetchAsDataUrl(url);
		} catch (error) {
			lastError = error;
			if (attempt < REPORT_IMAGE_ATTEMPTS) {
				await new Promise((resolve) => setTimeout(resolve, retryDelayMs * attempt));
			}
		}
	}
	throw new ReportImageLoadError(url, lastError);
}

/** Resolves every image URL to an inline data URL, keyed exactly as given. Rejects if any image can't be loaded. */
export async function loadReportImages(
	images: Record<string, string>,
	options: { retryDelayMs?: number } = {}
): Promise<Record<string, string>> {
	const retryDelayMs = options.retryDelayMs ?? RETRY_DELAY_MS;
	const pq = new IkePromiseQueue(Logger, REPORT_IMAGE_CONCURRENCY);
	const entries = await Promise.all(
		Object.entries(images).map(([reference, url]) =>
			url.startsWith('data:')
				? Promise.resolve([reference, url] as const)
				: pq.addAndReturn(async () => [reference, await fetchWithRetry(url, retryDelayMs)] as const)
		)
	);
	return Object.fromEntries(entries);
}
